import { memo, useCallback } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Eye, EyeOff, GripHorizontal } from 'lucide-react'
import { CommandItem } from '@/components/ui/command'
import { cn } from '@/lib/utils'

export interface ColumnItem {
  id: string
  label: string
  isVisible: boolean
}

interface SortableItemProps {
  column: ColumnItem
  isVisible: boolean
  onToggle: (columnId: string) => void
}

const SortableItem = memo(
  ({ column, onToggle, isVisible }: SortableItemProps) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: column.id,
    })

    const style = {
      transform: CSS.Translate.toString(transform),
      transition,
    }

    const handleSelect = useCallback(() => {
      onToggle(column.id)
    }, [onToggle, column.id])

    return (
      <CommandItem
        ref={setNodeRef}
        style={style}
        value={column.label}
        onSelect={handleSelect}
        className={cn(
          'flex items-center justify-between cursor-pointer py-1.5',
          isDragging && 'opacity-50',
          !isVisible && 'opacity-60'
        )}
      >
        <div className="flex items-center gap-2">
          <div
            {...attributes}
            {...listeners}
            className="flex items-center justify-center cursor-grab active:cursor-grabbing group"
            onClick={(e) => e.stopPropagation()}
          >
            <GripHorizontal className="size-4 text-primary/10 transition-colors group-hover:text-primary" />
          </div>
          <span
            className={cn(
              'text-xs font-normal',
              isVisible ? 'text-primary' : 'text-muted-foreground'
            )}
          >
            {column.label}
          </span>
        </div>
        {isVisible ? (
          <Eye className="size-4 text-primary" />
        ) : (
          <EyeOff className="size-4 text-primary" />
        )}
      </CommandItem>
    )
  }
)

SortableItem.displayName = 'SortableItem'

export { SortableItem }
